import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Loader2, Send, Save, Settings, MessageSquare, Inbox, Check } from 'lucide-react';
import { ai, AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import {
    supabase,
    supabase_get_whatsapp_context_for_candidates,
    supabase_save_candidate_communication,
} from '../services/supabaseService';
import {
    buildWhatsAppPersonalizeContext,
    personalizeWhatsAppBody,
    type CandidateWhatsAppVacancyContext,
} from '../utils/whatsappShortcodes';

export interface BulkCandidate {
    id: string;
    profile_id?: string;
    name: string;
    email: string;
    phone?: string;
    vacancyTitle?: string;
    companyName?: string;
}

interface BulkWhatsAppComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCandidates: BulkCandidate[];
  vacancyData?: any;
  customTemplates?: any[];
  initialBody?: string;
  onSuccessCallback?: () => void;
}

export const BulkWhatsAppComposerModal: React.FC<BulkWhatsAppComposerModalProps> = ({ isOpen, onClose, selectedCandidates, vacancyData, customTemplates, initialBody, onSuccessCallback }) => {
  const [body, setBody] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showDirectConfirm, setShowDirectConfirm] = useState(false);
  const [directSendIndex, setDirectSendIndex] = useState(-1);
  const [whatsappOpened, setWhatsappOpened] = useState(false);
  
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [vacancyContextByCandidateId, setVacancyContextByCandidateId] = useState<
      Record<string, CandidateWhatsAppVacancyContext>
  >({});

  const selectedCandidateIdsKey = useMemo(
      () =>
          selectedCandidates
              .map((c) => String(c.id || '').trim())
              .filter(Boolean)
              .sort()
              .join(','),
      [selectedCandidates]
  );

  useEffect(() => {
      if (!isOpen) {
          setVacancyContextByCandidateId({});
          return;
      }
      if (vacancyData) return;

      const ids = selectedCandidateIdsKey ? selectedCandidateIdsKey.split(',') : [];
      if (ids.length === 0) return;

      let cancelled = false;
      void supabase_get_whatsapp_context_for_candidates(ids).then((map) => {
          if (!cancelled) setVacancyContextByCandidateId(map);
      });
      return () => {
          cancelled = true;
      };
  }, [isOpen, vacancyData, selectedCandidateIdsKey]);

  useEffect(() => {
     if (isOpen) {
         if (initialBody) {
             setBody(initialBody);
         }
         if (customTemplates) {
             setTemplates(customTemplates);
         } else {
             const fetchClientData = async () => {
                 const { data: authData } = await supabase.auth.getUser();
                 if (authData?.user) {
                     const { data: profileData } = await supabase.from('profiles').select('client_id').eq('id', authData.user.id).single();
                     let targetClientId = profileData?.client_id;
                     
                     if (!targetClientId) return;

                     let query = supabase.from('clients').select('data').eq('id', targetClientId);
                     const { data } = await query.limit(1).single();
                     
                     if (data?.data?.whatsapp_templates) {
                         setTemplates(data.data.whatsapp_templates);
                     }
                 }
             };
             fetchClientData();
         }
     } else {
         setBody('');
         setSelectedTemplateId('');
         setIsSuccess(false);
     }
  }, [isOpen, initialBody, customTemplates]);

  const handleTemplateSelect = (tplId: string) => {
      setSelectedTemplateId(tplId);
      const tpl = templates.find(t => t.id === tplId);
      if (tpl) {
          setBody(tpl.body || '');
      } else if (tplId === '') {
          setBody('');
      }
  };

  const getPersonalizedBody = (c: BulkCandidate) => {
      const resolved = vacancyContextByCandidateId[c.id];
      const enriched: BulkCandidate = {
          ...c,
          vacancyTitle: c.vacancyTitle || resolved?.vacancyTitle,
          companyName: c.companyName || resolved?.companyName,
      };
      const vacCtx = vacancyData ?? resolved?.vacancyData;
      return personalizeWhatsAppBody(body, buildWhatsAppPersonalizeContext(enriched, vacCtx));
  };

  if (!isOpen) return null;

  const validCandidates = selectedCandidates.filter(c => c.phone);

  if (isSuccess) {
      return createPortal(
          <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm relative flex flex-col items-center justify-center p-8 animate-scaleIn border border-slate-200 text-center">
                  <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-5 ring-8 ring-emerald-50">
                      <Check size={40} />
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-2">Gelukt!</h2>
                  <p className="text-slate-500 text-sm font-medium mb-8 leading-relaxed">
                      Er staan <strong>{validCandidates.length} berichten</strong> voor je klaar in je Postvak UIT (Recruiter Hub).
                  </p>
                  <button 
                      onClick={onClose}
                      className="w-full py-4 bg-slate-900 hover:bg-black text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg hover:shadow-xl active:scale-95"
                  >
                      Sluiten
                  </button>
              </div>
          </div>,
          document.body
      );
  }
  
  
  if (directSendIndex >= 0 && directSendIndex < validCandidates.length) {
      const c = validCandidates[directSendIndex];
      let phone = c.phone || '';
      if (phone.startsWith('0')) phone = '+31' + phone.substring(1);
      phone = phone.replace(/\D/g, '');
      const personalizedBody = getPersonalizedBody(c);

      return createPortal(
          <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
              <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm relative flex flex-col items-center justify-center p-8 animate-scaleIn border border-slate-200 text-center">
                  <div className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-4">
                      Bericht {directSendIndex + 1} van {validCandidates.length}
                  </div>
                  <div className="w-20 h-20 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center mb-5 ring-8 ring-blue-50">
                      <MessageSquare size={40} />
                  </div>
                  <h2 className="text-xl font-black text-slate-900 mb-2 truncate w-full">{c.name}</h2>
                  
                  {!whatsappOpened ? (
                      <>
                          <p className="text-slate-500 text-sm font-medium mb-8 leading-relaxed">
                              Klaar om het bericht naar {c.name.split(' ')[0]} te sturen?
                          </p>
                          <div className="flex flex-col gap-3 w-full">
                              <button 
                                  onClick={() => {
                                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(personalizedBody)}`, '_blank');
                                      setWhatsappOpened(true);
                                  }}
                                  className="w-full py-4 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg hover:shadow-xl active:scale-95"
                              >
                                  Open WhatsApp
                              </button>
                              <button 
                                  onClick={() => setDirectSendIndex(-1)}
                                  className="w-full py-4 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-sm active:scale-95"
                              >
                                  Annuleren
                              </button>
                          </div>
                      </>
                  ) : (
                      <>
                          <p className="text-slate-500 text-sm font-medium mb-8 leading-relaxed">
                              Is het bericht via WhatsApp succesvol verzonden?
                          </p>
                          <div className="flex flex-col gap-3 w-full">
                              <button 
                                  onClick={async () => {
                                      setIsGenerating(true);
                                      try {
                                          const { data: userData } = await supabase.auth.getUser();
                                          await supabase_save_candidate_communication(
                                              c.profile_id || c.id,
                                              userData?.user?.id || 'system',
                                              personalizedBody,
                                              'whatsapp'
                                          );
                                          
                                          if (directSendIndex + 1 >= validCandidates.length) {
                                              if (onSuccessCallback) await onSuccessCallback();
                                              setDirectSendIndex(-1);
                                              setIsSuccess(true);
                                          } else {
                                              setDirectSendIndex(directSendIndex + 1);
                                              setWhatsappOpened(false);
                                          }
                                      } catch (err) {
                                          console.error('Failed to log direct whatsapp', err);
                                          alert("Kon actie niet registreren.");
                                      } finally {
                                          setIsGenerating(false);
                                      }
                                  }}
                                  disabled={isGenerating}
                                  className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50"
                              >
                                  {isGenerating ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Ja, gelukt (Volgende)"}
                              </button>
                              <button 
                                  onClick={() => {
                                      if (directSendIndex + 1 >= validCandidates.length) {
                                          setDirectSendIndex(-1);
                                      } else {
                                          setDirectSendIndex(directSendIndex + 1);
                                          setWhatsappOpened(false);
                                      }
                                  }}
                                  disabled={isGenerating}
                                  className="w-full py-4 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-sm active:scale-95 disabled:opacity-50"
                              >
                                  Nee, overslaan
                              </button>
                          </div>
                      </>
                  )}
              </div>
          </div>,
          document.body
      );
  }


  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
        const { data: userData } = await supabase.auth.getUser();
        const recruiterName = userData?.user?.user_metadata?.name || 'Recruiter';
        
        const prompt = `Je bent een ervaren en overtuigende recruitment copywriter. Je schrijft namens de recruiter (${recruiterName}).
Schrijf een wervend, kort en krachtig WhatsApp-bericht om kandidaten te interesseren.

${body.trim() ? `De recruiter heeft al het volgende concept of instructies geschreven. Verbeter dit bericht of gebruik deze instructies als basis:\n"""\n${body.trim()}\n"""` : ''}

Context van vacature:
${vacancyData?.title ? 'Titel: ' + vacancyData.title : ''}
${vacancyData?.description?.substring(0, 1500) || 'Geen verdere context beschikbaar.'}

Regels:
1. CRUCIAAL: Gebruik in je bericht EXACT de volgende shortcodes (inclusief blokhaken). Vul ze NIET zelf in, maar typ letterlijk de blokhaken!
   VOUDT: "Hoi Jan," of "voor de rol van Developer"
   GOED: "Hoi [Voornaam]," of "voor de rol van [Vacature] bij [Bedrijf]"
   - Typ altijd letterlijk: [Voornaam]
   - Typ altijd letterlijk: [Vacature]
   - Typ altijd letterlijk: [Bedrijf]
2. Spreek namens de recruiter, dus NIET namens "Froukje". Praat in de ik-vorm als recruiter.
3. Gebruik korte alinea's, vlot taalgebruik en passende professionele/enthousiaste emoji's (zoals 👋, 🚀, 💼 of ☕).
4. Eindig met een duidelijke Call to Action ("Zullen we even bellen? 📞").
5. Formatteer in tekst (geen HTML), optioneel *vet* voor accenten (WhatsApp Markdown).
6. Afzender: Sluit het bericht af met exact de tekst: "Groeten,\\n${recruiterName}".
7. Schrijf GEEN extra toelichting of AI introductie, schrijf direct en uitsluitend het uiteindelijke WhatsApp-bericht.`;

        const response = await ai.models.generateContent({
            model: AI_PROXY_CHAT_MODEL,
            contents: prompt
        });

        if (response && response.text) {
             setBody(response.text.trim());
        }
    } catch (e) {
        console.error("AI Generation failed", e);
        alert("Kon geen bericht genereren. Probeer het later opnieuw.");
    } finally {
        setIsGenerating(false);
    }
  };

  const handleSaveToOutbox = async () => {
     if (!body.trim() || validCandidates.length === 0) return;
     
     setIsGenerating(true); // Re-use loading state for saving
     try {
         const { data: userData } = await supabase.auth.getUser();
         if (!userData?.user) throw new Error("Not logged in");

         const todosToInsert = validCandidates.map((candidate) => {
             const personalizedBody = getPersonalizedBody(candidate);

             return {
                 title: `[OUTBOX] WhatsApp: ${candidate.name}`,
                 description: personalizedBody,
                 status: 'Pending',
                 priority: 'medium',
                 user_id: userData.user.id,
                 candidate_id: candidate.id,
                 vacancy_id: vacancyData?.id || null,
                 quick_link_url: `/?vacancy=${vacancyData?.id || ''}&candidate=${candidate.id}`,
                 quick_link_title: `Kandidaat: ${candidate.name}`
             };
         });

         const { error } = await supabase.from('todos').insert(todosToInsert);
         if (error) throw error;

         setIsSuccess(true);
     } catch (e) {
         console.error("Save to outbox failed", e);
         alert("Opslaan naar outbox mislukt!");
     } finally {
         setIsGenerating(false);
     }
  };

  return createPortal(
    <div className="fixed inset-0 z-[500] flex justify-end bg-slate-900/40 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div 
        className="bg-white shadow-[0_0_40px_rgba(0,0,0,0.1)] w-[550px] max-w-full h-full flex flex-col transform transition-transform duration-300 translate-x-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
         <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-emerald-50 shrink-0">
             <div className="flex items-center gap-3">
                 <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shadow-inner text-emerald-600">
                    <MessageSquare size={18} />
                 </div>
                 <div>
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Bulk WhatsApp Studio</h2>
                    <p className="text-slate-500 font-medium text-xs">Stuur appjes naar {validCandidates.length} kandidaat{validCandidates.length !== 1 && 'en'}</p>
                 </div>
             </div>
             <button onClick={onClose} className="p-2 bg-white hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-full transition-colors border border-slate-200">
                <X size={20} />
             </button>
         </div>

         <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50 flex flex-col gap-6">
             {/* Left panel */}
             <div className="flex-1 flex flex-col gap-6">
                 
                 <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1.5 block">Kies WhatsApp Template</label>
                        <select 
                            className="w-full border border-slate-200 rounded-xl p-3 bg-white text-sm font-medium outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 shadow-sm"
                            value={selectedTemplateId}
                            onChange={(e) => handleTemplateSelect(e.target.value)}
                        >
                            <option value="">-- Geen Sjabloon (Vrije Tekst) --</option>
                            
                            {templates.filter(t => {
                                const s = (t as any).pipelineStatuses || ((t as any).pipelineStatus ? [(t as any).pipelineStatus] : []);
                                return s.length === 0;
                            }).map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}

                            {['Shortlist', 'Voorgesteld', 'Gesprek', 'Aanbod', 'Plaatsing', 'Rejected'].map(status => {
                                const matchingTemplates = templates.filter(t => {
                                    const s = (t as any).pipelineStatuses || ((t as any).pipelineStatus ? [(t as any).pipelineStatus] : []);
                                    return s.includes(status);
                                });
                                if (matchingTemplates.length === 0) return null;
                                return (
                                    <optgroup key={status} label={`Fase: ${status === 'Rejected' ? 'Afgewezen' : status}`}>
                                        {matchingTemplates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>
                    </div>

                    <div className="pt-5">
                       <button
                           onClick={handleGenerateAI}
                           disabled={isGenerating}
                           className="flex items-center gap-2 px-5 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all border border-indigo-200 shadow-sm disabled:opacity-50"
                       >
                           {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                           {isGenerating ? 'Froukje denkt...' : 'Froukje Magic'}
                       </button>
                    </div>
                 </div>

                 <div className="group flex flex-col flex-1 min-h-[300px]">
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">WhatsApp Bericht</label>
                        <span className="text-[10px] text-slate-400 font-bold px-2 py-1 bg-white border border-slate-200 rounded-md flex flex-wrap gap-1 items-center">
                            Ondersteunt: 
                            {['[Voornaam]', '[Naam]', '[Achternaam]', '[Vacature]', '[Bedrijf]', '[Telefoonnummer]', '[Email]', '{{name}}'].map(sc => (
                                <button key={sc} onClick={() => setBody(prev => prev + sc)} className="hover:text-emerald-600 hover:bg-emerald-50 px-1 py-0.5 rounded transition-colors text-indigo-500 bg-indigo-50/50 border border-indigo-100 shadow-sm">{sc}</button>
                            ))}
                        </span>
                    </div>
                    <textarea 
                        className="flex-1 bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl p-4 text-sm resize-none shadow-sm transition-all"
                        placeholder="Typ hier het appje voor de kandidaten..."
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                    />
                    {validCandidates.length === 1 && body.trim() && (
                        <div className="mt-2 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
                            <p className="text-[9px] font-black uppercase tracking-widest text-emerald-800 mb-1">
                                Voorbeeld voor {validCandidates[0].name.split(' ')[0]}
                            </p>
                            <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {getPersonalizedBody(validCandidates[0])}
                            </p>
                        </div>
                    )}
                 </div>
             </div>

             {/* Right Context Panel */}
             <div className="w-64 flex flex-col gap-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 overflow-hidden">
                    <div className="pb-3 border-b border-slate-100 mb-3">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                            Kandidaten Lijst <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px]">{validCandidates.length}</span>
                        </h4>
                    </div>
                    <ul className="space-y-2 max-h-[250px] overflow-y-auto custom-scrollbar pr-2">
                        {selectedCandidates.map((c, index) => (
                            <li key={`${c.id}-${index}`} className="text-xs font-bold text-slate-700 flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full ${c.phone ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                                <span className={!c.phone ? "text-slate-400 line-through" : ""}>{c.name}</span>
                            </li>
                        ))}
                    </ul>
                    {selectedCandidates.length !== validCandidates.length && (
                        <p className="text-[10px] text-red-500 font-bold mt-3 bg-red-50 p-2 rounded-lg leading-relaxed">
                            {selectedCandidates.length - validCandidates.length} geselecteerde {selectedCandidates.length - validCandidates.length === 1 ? 'kandidaat heeft' : 'kandidaten hebben'} geen geldig mobiel nummer en {selectedCandidates.length - validCandidates.length === 1 ? 'wordt' : 'worden'} overgeslagen.
                        </p>
                    )}
                </div>

                <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-2">Tips</h4>
                    <ul className="text-[10px] font-medium text-emerald-700 space-y-1.5 leading-relaxed">
                        <li>• Houd het informeel en kort.</li>
                        <li>• Gebruik [Voornaam], [Bedrijf], [Vacature] of ook [voornaam], {'{{name}}'}, etc.</li>
                        <li>• Berichten worden veilig opgeslagen in je OUTBOX.</li>
                        <li>• Verzend ze per stuk in je eigen tempo via de Recruiter Hub!</li>
                    </ul>
                </div>
             </div>
         </div>

         {/* Footer Actions */}
         <div className="p-6 border-t border-slate-100 bg-white flex justify-between items-center z-10">
             <button onClick={onClose} className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors">
                Annuleren
             </button>
             
             <div className="flex gap-3">
                 <button 
                     onClick={handleSaveToOutbox}
                     disabled={!body.trim() || validCandidates.length === 0 || isGenerating}
                     className="flex items-center gap-2 px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                    {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Inbox size={16} />} Opslaan in Outbox
                 </button>
                 
                 <button
                         onClick={() => {
                             if (!body.trim()) return;
                             setDirectSendIndex(0);
                             setWhatsappOpened(false);
                         }}
                         disabled={!body.trim() || isGenerating}
                         className="flex items-center gap-2 px-8 py-2.5 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        <Send size={16} /> Verstuur direct ({validCandidates.length})
                     </button>
             </div>
         </div>
      </div>
    </div>,
    document.body
  );
};
